import React from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, IconButton, Drawer, Divider, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import HubIcon from '@mui/icons-material/Hub';
import MessageIcon from '@mui/icons-material/Message';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import Header from '../Header';
import { useAppState } from '../../contexts/AppStateContext';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const DRAWER_WIDTH = 220;
const CONTENT_MAX_WIDTH = 1200;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'ダッシュボード', path: '/admin-panel', icon: <DashboardIcon /> },
  { label: 'ゲーム管理', path: '/admin-panel/games', icon: <SportsEsportsIcon /> },
  { label: 'Hub管理', path: '/admin-panel/hub', icon: <HubIcon /> },
  { label: 'メッセージ管理', path: '/admin-panel/messages', icon: <MessageIcon /> },
  { label: 'メンテナンス', path: '/admin-panel/maintenance', icon: <BuildIcon /> },
  { label: 'サイト通知', path: '/admin-panel/site-notice', icon: <NotificationsActiveIcon /> },
];

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [wideMode, setWideMode] = React.useState(() => localStorage.getItem('admin_wide') === '1');
  const navigate = useNavigate();
  const location = useLocation();

  const toggleWide = () => {
    setWideMode(prev => {
      const next = !prev;
      localStorage.setItem('admin_wide', next ? '1' : '0');
      return next;
    });
  };

  const isActive = (path: string) => {
    if (path === '/admin-panel') {
      return location.pathname === '/admin-panel';
    }
    return location.pathname.startsWith(path);
  };

  const sidebar = (
    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography
        variant="subtitle2"
        sx={{ px: 2, pb: 1, color: 'text.secondary', fontSize: '0.7rem', letterSpacing: 1 }}
      >
        ADMIN
      </Typography>
      <List dense disablePadding>
        {navItems.map((item) => (
          <ListItemButton
            key={item.path}
            selected={isActive(item.path)}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setMobileOpen(false);
            }}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: isActive(item.path) ? 'primary.main' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.8rem',
                fontWeight: isActive(item.path) ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}
      </List>
      {!isMobile && (
        <Box sx={{ mt: 'auto', px: 1, pb: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Tooltip title={wideMode ? '標準幅に戻す' : '全幅モード'} placement="right">
            <ListItemButton
              onClick={toggleWide}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                {wideMode ? <UnfoldLessIcon sx={{ transform: 'rotate(90deg)' }} /> : <UnfoldMoreIcon sx={{ transform: 'rotate(90deg)' }} />}
              </ListItemIcon>
              <ListItemText
                primary={wideMode ? '標準幅' : '全幅'}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItemButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  const { maintenance } = useAppState();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      {maintenance.enabled && (
        <Box sx={{
          bgcolor: 'error.dark',
          color: 'white',
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          position: 'fixed',
          top: { xs: 56, sm: 64 },
          left: 0,
          right: 0,
          zIndex: 1100,
          fontSize: '0.8rem',
        }}>
          <WarningAmberIcon sx={{ fontSize: '1rem' }} />
          メンテナンスモードが有効です — 一般ユーザーはアクセスできません
        </Box>
      )}
      <Box sx={{
        display: 'flex',
        flex: 1,
        paddingTop: maintenance.enabled ? { xs: '88px', sm: '96px' } : { xs: '56px', sm: '64px' },
      }}>
        {isMobile ? (
          <>
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1200, bgcolor: 'background.paper', boxShadow: 3 }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.default' } }}
            >
              {sidebar}
            </Drawer>
          </>
        ) : (
          <Box
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            {sidebar}
          </Box>
        )}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: 1, sm: 3 },
          maxWidth: wideMode ? 'none' : CONTENT_MAX_WIDTH,
          mx: wideMode ? 0 : 'auto',
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
